import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Table } from "reactstrap";

const DataTable = ({
    data = [],
    columns,
    keyField = "id",
    emptyMessage = "Nenhum registro encontrado",
    emptySubMessage = "",
    isLoading = false,
    noCard = false,
    expandableContent = null,
    onRowClick = null
}) => {
    const [expandedRows, setExpandedRows] = useState(new Set());

    const toggleExpand = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reset pagination when data length or rowsPerPage changes
    useEffect(() => {
        setPage(1);
    }, [data.length, rowsPerPage]);

    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;

    // Ensure the current page is within valid range
    const currentPage = Math.min(Math.max(1, page), totalPages);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return data.slice(startIndex, startIndex + rowsPerPage);
    }, [data, currentPage, rowsPerPage]);

    const canGoBack = currentPage > 1;
    const canGoForward = currentPage < totalPages;

    return (
        <div className={noCard ? "px-4 pb-4 pt-4" : ""}>

            {/* Top Toolbar: Rows per page selector */}
            {data.length > 0 && (
                <div className={`py-3 ${noCard ? "bg-transparent pb-3 pt-0" : "px-4 bg-white border-bottom"} d-flex justify-content-start align-items-center`}>
                    <style>{`
                        .cashier-select-compact {
                            padding: 0 20px 0 8px !important;
                            width: 110px !important;
                            height: 38px !important;
                            font-size: 13px !important;
                            line-height: normal !important;
                        }
                    `}</style>
                    <div className="d-flex align-items-center mb-0 text-muted font-size-13">
                        <span className="me-2 fw-medium text-uppercase text-dark font-size-12" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px", fontWeight: 800 }}>Mostrar</span>
                        <select
                            className="form-select form-select-sm border bg-white rounded-3 py-1 px-2 font-size-12 text-muted fw-bold cashier-select-compact"
                            style={{ borderColor: 'rgba(72, 39, 18, 0.15)', cursor: 'pointer' }}
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span className="ms-2 fw-medium text-uppercase text-dark font-size-12" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px", fontWeight: 800 }}>itens por página</span>
                    </div>
                </div>
            )}

            {/* Table Core */}
            <div className={`table-responsive ${noCard ? "shadow-sm bg-white" : ""}`}>
                <Table className="align-middle table-nowrap table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} className={`${col.headerClassName || ""}`} style={col.style}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-5">
                                    <i className="mdi mdi-loading mdi-spin font-size-24 text-primary"></i>
                                    <p className="mb-0 mt-2 fw-bold font-size-12 text-muted">Carregando...</p>
                                </td>
                            </tr>
                        ) : paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => {
                                const rowKey = row[keyField] || rowIndex;
                                const isExpanded = expandedRows.has(rowKey);
                                return (
                                <React.Fragment key={rowKey}>
                                    <tr 
                                        className={(expandableContent || onRowClick) ? "cursor-pointer" : ""} 
                                        onClick={() => {
                                            if (expandableContent) toggleExpand(rowKey);
                                            if (onRowClick) onRowClick(row);
                                        }}
                                    >
                                        {columns.map((col, idx) => (
                                            <td key={idx} className={col.className} style={col.cellStyle}>
                                                {col.render ? col.render(row, rowIndex, { page: currentPage, rowsPerPage, isExpanded }) : row[col.key]}
                                            </td>
                                        ))}
                                    </tr>
                                    {expandableContent && isExpanded && (
                                        <tr>
                                            <td colSpan={columns.length} className="p-0 border-0 bg-light">
                                                {expandableContent(row)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-5">
                                    <div>
                                        <i className="mdi mdi-table-off mdi-48px d-block mb-2 text-muted" style={{ opacity: 0.4 }}></i>
                                        <h6 className="fw-bold text-dark">{emptyMessage}</h6>
                                        {emptySubMessage && <p className="mb-0 small text-muted">{emptySubMessage}</p>}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Bottom Toolbar: Compact Premium Outono Pagination Control */}
            {data.length > 0 && (
                <div className={`${noCard ? "py-2 bg-transparent" : "px-4 py-3 bg-white border-top"} d-flex justify-content-end align-items-center`}>
                    <div className="d-flex align-items-center gap-3">
                        <span className="text-muted mx-2 font-size-12 fw-bold">
                            {(currentPage - 1) * rowsPerPage + 1} - {Math.min(currentPage * rowsPerPage, totalRows)} de {totalRows}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="border rounded-3 p-1 bg-white btn btn-light btn-sm"
                                style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                disabled={!canGoBack}
                                onClick={() => setPage(currentPage - 1)}
                            >
                                <i className="mdi mdi-chevron-left text-muted font-size-16" />
                            </button>
                            <button
                                className="border rounded-3 p-1 bg-white btn btn-light btn-sm"
                                style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                disabled={!canGoForward}
                                onClick={() => setPage(currentPage + 1)}
                            >
                                <i className="mdi mdi-chevron-right text-muted font-size-16" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

DataTable.propTypes = {
    data: PropTypes.array,
    columns: PropTypes.array.isRequired,
    keyField: PropTypes.string,
    emptyMessage: PropTypes.string,
    emptySubMessage: PropTypes.string,
    isLoading: PropTypes.bool,
    expandableContent: PropTypes.func
};


export default DataTable;
